import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ImageUploading from "react-images-uploading";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import CloseIcon from "@material-ui/icons/Close";
import Button from "../../../../components/Button";
import { Field } from "redux-form";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		width: "90%",
		maxWidth: 860,
		padding: 20,
	},
	header: {
		display: "flex",
		alignItems: "center",
	},
	title: {
		padding: 0,
		"& h2": {
			fontSize: 18,
			fontWeight: 600,
		},
	},
	closeIcon: {
		marginRight: 12,
		color: "#476282",
		cursor: "pointer",
	},
	content: {
		padding: 0,
	},
	field: {
		marginTop: 14,
		"& .MuiOutlinedInput-root": {
			background: "#fff",
		},
	},
	textArea: {
		marginTop: 14,
		"& .MuiOutlinedInput-root": {
			background: "#fff",
		},
	},
	row: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginTop: 16,
		padding: "10px 12px",
		background: "#f4f7fc",
		borderRadius: 8,
	},
	uploadRow: {
		display: "flex",
		gap: 12,
		marginTop: 16,
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
		},
	},
	uploadCard: {
		flex: 1,
		padding: 12,
		border: "1px solid #d9e1ee",
		borderRadius: 10,
		background: "#fff",
	},
	preview: {
		marginTop: 10,
		width: "100%",
		height: 150,
		backgroundSize: "cover",
		backgroundPosition: "center",
		borderRadius: 8,
		border: "1px solid #e6ebf3",
	},
	footer: {
		marginTop: 18,
		display: "flex",
		justifyContent: "center",
	},
	saveButton: {
		minWidth: 210,
	},
	hint: {
		marginTop: 8,
		fontSize: 13,
		color: "#6b7c93",
	},
	productsWrap: {
		marginTop: 14,
		padding: 12,
		background: "#f4f7fc",
		borderRadius: 8,
	},
	productsInputRow: {
		display: "flex",
		alignItems: "center",
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
			alignItems: "stretch",
		},
	},
	productInputField: {
		flex: 1,
		"& .MuiOutlinedInput-root": {
			height: 45,
			background: "#fff",
		},
		"& .MuiOutlinedInput-input": {
			padding: "12px 14px",
			lineHeight: "21px",
			boxSizing: "border-box",
		},
	},
	productAddButton: {
		"&&": {
			minWidth: 180,
			height: 45,
			marginTop: 0,
			marginLeft: 10,
		},
		[theme.breakpoints.down("sm")]: {
			"&&": {
				width: "100%",
				marginLeft: 0,
				marginTop: 10,
			},
		},
	},
	productsList: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
		marginTop: 10,
	},
	productChip: {
		background: "#e0e9ff",
		color: "#326dff",
	},
}));

const defaultBanner = {
	title: "",
	description: "",
	isAvailable: true,
	smallPhoto: "",
	largePhoto: "",
	productsId: [],
};

const BannerEditorDialog = ({ open, mode, banner, onClose, onSubmit }) => {
	const classes = useStyles();
	const [formData, setFormData] = useState(defaultBanner);
	const [productIdInput, setProductIdInput] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!open) return;

		const productsId = Array.isArray(banner && banner.productsId)
			? banner.productsId
					.map((id) => String(id || "").trim())
					.filter((id) => !!id)
			: [];

		setFormData({
			...defaultBanner,
			...(banner || {}),
			productsId,
			isAvailable:
				banner && banner.isAvailable !== undefined
					? banner.isAvailable
					: banner && banner.available !== undefined
					? banner.available
					: true,
		});
		setProductIdInput("");
	}, [open, banner]);

	const title = useMemo(
		() => (mode === "edit" ? "Редактирование баннера" : "Добавление баннера"),
		[mode]
	);

	const uploadImage = async (file) => {
		const login = localStorage.getItem("@login");
		if (!file || !login) return "";

		const body = new FormData();
		body.append("photo", file);
		body.append("login", login);
		body.append("folder", "loyalty-system");

		const response = await axios.post("https://e-admin.com.ua/photo/index.php", body);
		const imageName =
			response && response.data && response.data.image_name
				? response.data.image_name.split(" ")[0]
				: null;

		if (!imageName) throw new Error("Ошибка загрузки фото");
		return `https://e-admin.com.ua/photo/photo/uploads/${login}/loyalty-system/${imageName}`;
	};

	const handleUpload = async (type, imageList) => {
		try {
			setIsLoading(true);
			const file = imageList && imageList[0] && imageList[0].file;
			const url = await uploadImage(file);
			setFormData((prev) => ({ ...prev, [type]: url }));
		} catch (error) {
			alert("Ошибка загрузки изображения");
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = () => {
		if (!formData.largePhoto) {
			alert("Загрузите большое изображение баннера");
			return;
		}

		const productsId = Array.isArray(formData.productsId)
			? formData.productsId
					.map((id) => String(id || "").trim())
					.filter((id) => !!id)
			: [];

		onSubmit({
			...formData,
			smallPhoto: formData.smallPhoto || "",
			productsId,
			available: !!formData.isAvailable,
			isAvailable: !!formData.isAvailable,
		});
	};

	const handleAddProductId = () => {
		const nextId = String(productIdInput || "").trim();
		if (!nextId) return;

		setFormData((prev) => {
			const current = Array.isArray(prev.productsId) ? prev.productsId : [];
			if (current.includes(nextId)) return prev;
			return { ...prev, productsId: [...current, nextId] };
		});
		setProductIdInput("");
	};

	const handleDeleteProductId = (idToDelete) => {
		setFormData((prev) => {
			const current = Array.isArray(prev.productsId) ? prev.productsId : [];
			return {
				...prev,
				productsId: current.filter((id) => id !== idToDelete),
			};
		});
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			classes={{ paper: classes.dialogPaper }}
			aria-labelledby="banner-editor-title"
		>
			<div className={classes.header}>
				<CloseIcon onClick={onClose} className={classes.closeIcon} />
				<DialogTitle id="banner-editor-title" className={classes.title}>
					{title}
				</DialogTitle>
			</div>
			<DialogContent className={classes.content}>
				<TextField
					className={classes.field}
					fullWidth
					variant="outlined"
					label="Название"
					value={formData.title}
					onChange={(e) => {
						const value = e.target.value;
						setFormData((prev) => ({ ...prev, title: value }));
					}}
				/>
				<TextField
					className={classes.textArea}
					fullWidth
					variant="outlined"
					label="Описание"
					multiline
					rows={4}
					value={formData.description}
					onChange={(e) => {
						const value = e.target.value;
						setFormData((prev) => ({ ...prev, description: value }));
					}}
				/>

				<div className={classes.row}>
					<div>Показывать баннер</div>
					<Switch
						checked={!!formData.isAvailable}
						onChange={() =>
							setFormData((prev) => ({ ...prev, isAvailable: !prev.isAvailable }))
						}
						color="primary"
					/>
				</div>

				<div className={classes.productsWrap}>
					<div style={{ marginBottom: 8, color: "#243447", fontWeight: 500 }}>
						ID товаров для баннера
					</div>
					<div className={classes.productsInputRow}>
						<TextField
							className={classes.productInputField}
							fullWidth
							variant="outlined"
							placeholder="Введите ID товара"
							value={productIdInput}
							onChange={(e) => setProductIdInput(e.target.value)}
							onKeyPress={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAddProductId();
								}
							}}
						/>
						<Button
							text="Добавить в список"
							handleClick={handleAddProductId}
							className={classes.productAddButton}
							load={false}
						/>
					</div>
					<div className={classes.productsList}>
						{(formData.productsId || []).map((id) => (
							<Chip
								key={id}
								label={id}
								onDelete={() => handleDeleteProductId(id)}
								className={classes.productChip}
							/>
						))}
					</div>
				</div>

				<div className={classes.uploadRow}>
					<div className={classes.uploadCard}>
						<ImageUploading
							value={[]}
							onChange={(list) => handleUpload("largePhoto", list)}
							maxNumber={1}
							acceptType={["jpg", "jpeg", "png"]}
							maxFileSize={512000}
							dataURLKey="data_url"
						>
							{({ onImageUpload }) => (
								<Button
									text="Загрузить большой баннер"
									handleClick={onImageUpload}
									className={classes.saveButton}
									load={isLoading}
								/>
							)}
						</ImageUploading>
						{formData.largePhoto && (
							<div
								className={classes.preview}
								style={{ backgroundImage: `url(${formData.largePhoto})` }}
							/>
						)}
						<div className={classes.hint}>Рекомендуемый размер: 650x900 (jpg, jpeg, png)</div>
					</div>
					<div className={classes.uploadCard}>
						<ImageUploading
							value={[]}
							onChange={(list) => handleUpload("smallPhoto", list)}
							maxNumber={1}
							acceptType={["jpg", "jpeg", "png"]}
							maxFileSize={512000}
							dataURLKey="data_url"
						>
							{({ onImageUpload }) => (
								<Button
									text="Загрузить маленький баннер"
									handleClick={onImageUpload}
									className={classes.saveButton}
									load={isLoading}
								/>
							)}
						</ImageUploading>
						{formData.smallPhoto && (
							<div
								className={classes.preview}
								style={{ backgroundImage: `url(${formData.smallPhoto})` }}
							/>
						)}
						<div className={classes.hint}>Рекомендуемый размер: 540x300 (jpg, jpeg, png)</div>
					</div>
				</div>

				<div className={classes.footer}>
					<Button
						text="Сохранить"
						handleClick={handleSave}
						className={classes.saveButton}
						load={false}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default BannerEditorDialog;
